import React, { memo, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import { DataGrid, GridCellModes } from "@mui/x-data-grid";
import { map, has } from "lodash";
import { ENTITY_TYPES } from "../../utils";
import useStableCallback from "../../hooks/useStableCallback";
import usePermissions from "../../hooks/usePermissions";
import getPogPlanogramColumns, {
  POG_POG_EDITABLE_FIELDS,
} from "./pogPogTableColumns";
import getOrderPogColumns from "./orderPogTableColumns";
import { validItem } from "../../utils/itemValidator";
import { SCOPES } from "../../permissions/permissions-map";

const PogDataGridSx = {
  "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer":
    {
      display: "none",
    },
  "& .MuiDataGrid-footerContainer": {
    display: "none",
  },
};

const PogDataTableSx = { height: 94, width: "100%" };

const DataGridExperimentalFeatures = { newEditingApi: true };

const PogTable = ({
  data,
  scope,
  parentId,
  updateSelections,
  getProcessedRow,
  serverSelections = [],
  retailerNotes = [],
  plannerNotes = [],
}) => {
  const store = useSelector((store) => store.global.store);
  const adjQtyError = useSelector((store) => store.global.adjQtyError);
  const entityType = useSelector((store) => store.global.settings.entityType);
  const restrictedPogs = useSelector(
    (store) => store.global.settings.restrictedPogs
  );
  const { hasPermission } = usePermissions();
  const rows = useMemo(() => [data], [data]);
  const [cellModesModel, setCellModesModel] = useState({});

  const restrictEdit = useMemo(
    () =>
      store?.pending ||
      store?.isArchived ||
      !hasPermission({ scopes: [SCOPES.canEdit] }),
    [store, hasPermission]
  );

  const pogTableColumns = useMemo(() => {
    switch (entityType) {
      case ENTITY_TYPES.planogram:
        return getPogPlanogramColumns(
          getProcessedRow,
          plannerNotes,
          retailerNotes,
          restrictEdit
        );
      case ENTITY_TYPES.orders:
        return getOrderPogColumns();
      default:
        return getPogPlanogramColumns(
          getProcessedRow,
          plannerNotes,
          retailerNotes,
          restrictEdit
        );
    }
  }, [entityType, getProcessedRow, plannerNotes, retailerNotes, restrictEdit]);

  const selectionModel = serverSelections?.includes(parentId) ? [parentId] : [];

  const handleSelectionModelChange = useStableCallback((newModel) => {
    if (adjQtyError) {
      return;
    }
    if (!newModel?.length) {
      return updateSelections([], parentId, scope);
    }
    let allItemSelections = [];
    if (ENTITY_TYPES.orders === entityType) {
      allItemSelections = map(data?.subRows, validItem).filter((i) => !!i);
    } else {
      allItemSelections = map(data?.subRows, "id");
    }
    return updateSelections([parentId, ...allItemSelections], parentId, scope);
  });

  const handleProcessRowUpdate = useStableCallback(async (updatedRow) => {
    if (adjQtyError) {
      return await Promise.reject();
    }
    getProcessedRow(updatedRow);
    return await Promise.resolve(updatedRow);
  });

  const handleCellClick = useStableCallback((params) => {
    setCellModesModel((prevModel) => {
      if (!params.isEditable) {
        return prevModel;
      }
      if (adjQtyError) {
        return prevModel;
      }
      return {
        // Revert the mode of the other cells from other rows
        ...Object.keys(prevModel).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prevModel[id]).reduce(
              (acc2, field) => ({
                ...acc2,
                [field]: { mode: GridCellModes.View },
              }),
              {}
            ),
          }),
          {}
        ),
        [params.id]: {
          // Revert the mode of other cells in the same row
          ...Object.keys(prevModel[params.id] || {}).reduce(
            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
            {}
          ),
          [params.field]: {
            mode: GridCellModes.Edit,
          },
        },
      };
    });
  });

  const handleCellModesModelChange = useStableCallback((newModel) => {
    if (adjQtyError) {
      return;
    }
    setCellModesModel(newModel);
  });

  const handleIsCellEditable = useStableCallback((params) => {
    return serverSelections.includes(params.row.id);
  });

  const getRowId = useStableCallback((params) => {
    return params?.id ?? params?.PlanogramNumber;
  });

  const isRowSelectable = useStableCallback((params) => {
    if (entityType === ENTITY_TYPES.orders) {
      const restrictedPogList = has(restrictedPogs, scope)
        ? restrictedPogs[scope]
        : [];
      return !restrictedPogList.includes(params?.row?.id);
    }
    //TODO: For other entities use condition as needed.
    return true;
  });

  const getEditCellClass = useStableCallback((params) => {
    if (POG_POG_EDITABLE_FIELDS.includes(params.field) && params.isEditable) {
      return "oso-editable-field";
    }
    return "";
  });

  return (
    <Box sx={PogDataTableSx}>
      <DataGrid
        sx={PogDataGridSx}
        rows={rows}
        columns={pogTableColumns}
        checkboxSelection
        disableColumnMenu
        disableSelectionOnClick
        experimentalFeatures={DataGridExperimentalFeatures}
        selectionModel={selectionModel}
        isRowSelectable={isRowSelectable}
        onSelectionModelChange={handleSelectionModelChange}
        processRowUpdate={handleProcessRowUpdate}
        cellModesModel={cellModesModel}
        onCellModesModelChange={handleCellModesModelChange}
        onCellClick={handleCellClick}
        isCellEditable={handleIsCellEditable}
        getCellClassName={getEditCellClass}
        getRowId={getRowId}
        hideFooterSelectedRowCount
        hideFooterPagination
        density="compact"
      />
    </Box>
  );
};

export default memo(PogTable);
